import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import ContactForm from "../components/ContactForm";

const ContactPage = () => {
  return (
    <main data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-[#020617] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#020617] to-[#172554] pointer-events-none" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="label-accent mb-4 block">Get In Touch</span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight text-white leading-[0.9] mb-6">
              Let's Start a
              <br />
              <span className="text-[#E53935]">Conversation</span>
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              Ready to elevate your business or sports organization? We'd love 
              to hear from you. Fill out the form below or reach out directly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-[#0F172A]">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-8">
                Contact Information
              </h2>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex gap-4" data-testid="contact-address">
                  <div className="w-12 h-12 rounded-sm bg-[#E53935]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#E53935]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white text-lg mb-1">
                      Office Location
                    </h3>
                    <p className="text-slate-400">
                      93 Hospital Rd. Sigma Building,<br />
                      Grand Cayman, Cayman Islands
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4" data-testid="contact-phone">
                  <div className="w-12 h-12 rounded-sm bg-[#E53935]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#E53935]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white text-lg mb-1">
                      Phone
                    </h3>
                    <a
                      href="tel:+13459289072"
                      className="text-slate-400 hover:text-[#E53935] transition-colors"
                    >
                      +1 (345) 928-9072
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4" data-testid="contact-email">
                  <div className="w-12 h-12 rounded-sm bg-[#E53935]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#E53935]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white text-lg mb-1">
                      Email
                    </h3>
                    <a
                      href="mailto:info@anglinconsultants.ky"
                      className="text-slate-400 hover:text-[#E53935] transition-colors"
                    >
                      info@anglinconsultants.ky
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex gap-4" data-testid="contact-hours">
                  <div className="w-12 h-12 rounded-sm bg-[#E53935]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#E53935]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white text-lg mb-1">
                      Business Hours
                    </h3>
                    <p className="text-slate-400">
                      Monday - Friday: 9:00 AM - 5:00 PM<br />
                      Saturday - Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-12 aspect-video bg-[#020617] border border-[#1E293B] overflow-hidden">
                <iframe
                  title="Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3731.5!2d-81.3755!3d19.2869!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8f2587891fda6c5d%3A0x7c3f8c9d8e8a1b2c!2s93%20Hospital%20Rd%2C%20George%20Town%2C%20Cayman%20Islands!5e0!3m2!1sen!2s!4v1708300000000!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="bg-[#020617] border border-[#1E293B] p-8 md:p-10">
                <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-2">
                  Send Us a Message
                </h2>
                <p className="text-slate-400 mb-8">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>
                <ContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-[#020617]">
        <div className="section-container">
          <div className="text-center mb-16">
            <span className="label-accent mb-4 block">FAQ</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              Common Questions
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: "What industries do you serve?",
                a: "We specialize in sports organizations, event companies, and businesses across various sectors in the Cayman Islands and Caribbean region.",
              },
              {
                q: "How long does a typical project take?",
                a: "Project timelines vary based on scope. Strategy projects typically take 4-8 weeks, while event development can span several months.",
              },
              {
                q: "Do you offer ongoing consulting?",
                a: "Yes, we offer retainer arrangements for organizations that need continuous strategic support and advisory services.",
              },
              {
                q: "What's your pricing structure?",
                a: "We provide customized proposals based on project scope. Contact us for a free initial consultation to discuss your needs.",
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#0F172A] border border-[#1E293B] p-6"
                data-testid={`faq-${index}`}
              >
                <h3 className="font-heading text-lg font-bold text-white mb-2">
                  {faq.q}
                </h3>
                <p className="text-slate-400 text-sm">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
